import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import CustomInput from "../../../components/CustomInput";
import CustomDropdown from "../../../components/CustomDropdown";
import CustomButton from "../../../components/CustomButton";
import Sidebar from "../../../components/Sidebar";
import {
  clearHomePageInfosClientCache,
  getHomePageInfos,
  getCashOutPaymentMethod,
  createCashOutOperation,
  ensureLocation,
} from "../../../utils/apiClient";
import TransactionPasswordDialog from "../../../components/TransactionPasswordDialog";
import StatusBanner from "../../../components/StatusBanner";
import AppConstants from "../../../utils/appConstants";
import WalletController from "../../../controller/walletController";
import { FeesFor } from "../../../utils/enums";
import {
  getPermissionControllers,
  hasCustomerPermission,
  hasBrokerPermission,
  isKycVerified,
  isAccountApprovalLocked,
} from "../../../utils/permissionHelper";
import PermissionCustomerModelController from "../../../controller/model/PermissionCustomerModelController";
import PermissionBrokerModelController from "../../../controller/model/PermissionBrokerModelController";
import { useToast } from "../../../contexts/ToastContext";
import { openSupportChatOnCurrentPage } from "../../../utils/supportChatNavigation";
import WalletCurrencyFlag from "../../../components/WalletCurrencyFlag";
import {
  formatCashWalletDropdownLabel,
  resolveCashWalletCurrencyDisplay,
} from "../../../utils/currencyFlagHelper";
import "../../css/cachIn.css";

/** Get min amount for cash-out. Checks RANGE.MIN first, then restrictions. */
/** IMPORTANT: MIN from RANGE must ALWAYS be respected - restriction only used if greater than MIN */
function getMinAmountCashOut(walletData) {
  // Get currency ID for restriction matching
  const cur = walletData?.currencies || walletData?.currency;
  if (!cur) return null;
  
  const currencyId = cur?.ID || cur?.CURRENCY_ID || walletData?.CURRENCY_ID;
  
  // FIRST: Get MIN from wallet's currencies.RANGE (this is ALWAYS the base requirement)
  let range = cur.RANGE;
  if (typeof range === "string") {
    try {
      range = JSON.parse(range);
    } catch {
      return null;
    }
  }
  if (!range || typeof range !== "object") return null;
  const minFromRange = range.MIN ?? range.min;
  if (minFromRange == null) {
    // No min from range, check if we have a restriction to use
    const restrictions = walletData?.currencies?.restriction || walletData?.restriction;
    if (restrictions && Array.isArray(restrictions)) {
      const feesFor = FeesFor.CASHOUT; // 1 for cash out
      const matchingRestriction = restrictions.find(rest => {
        const restFor = rest.FOR !== undefined ? Number(rest.FOR) : null;
        const restCurrencyId = rest.CURRENCY_ID !== undefined ? Number(rest.CURRENCY_ID) : null;
        return restFor === feesFor && 
               (currencyId == null || restCurrencyId == null || restCurrencyId === Number(currencyId));
      });
      if (matchingRestriction && matchingRestriction.AMOUNT !== undefined) {
        const restrictionAmount = parseFloat(matchingRestriction.AMOUNT);
        if (!isNaN(restrictionAmount)) {
          return restrictionAmount;
        }
      }
    }
    return null;
  }
  const num = Number(minFromRange);
  if (Number.isNaN(num)) {
    return null;
  }
  
  // SECOND: Check restrictions and compare with minFromRange
  // IMPORTANT: MIN from RANGE is ALWAYS the base requirement
  // Restriction is only used if it's GREATER than the MIN from range
  const restrictions = walletData?.currencies?.restriction || walletData?.restriction;
  if (restrictions && Array.isArray(restrictions)) {
    const feesFor = FeesFor.CASHOUT; // 1 for cash out
    const matchingRestriction = restrictions.find(rest => {
      const restFor = rest.FOR !== undefined ? Number(rest.FOR) : null;
      const restCurrencyId = rest.CURRENCY_ID !== undefined ? Number(rest.CURRENCY_ID) : null;
      return restFor === feesFor && 
             (currencyId == null || restCurrencyId == null || restCurrencyId === Number(currencyId));
    });
    
    if (matchingRestriction && matchingRestriction.AMOUNT !== undefined) {
      const restrictionAmount = parseFloat(matchingRestriction.AMOUNT);
      if (!isNaN(restrictionAmount)) {
        // If restriction amount > min from range, use restriction amount
        if (restrictionAmount > num) {
          return restrictionAmount;
        } else {
          return num;
        }
      }
    }
  }
  
  // Return min from range (always required - must be greater than this)
  return num;
}

/** Get wallet balance from wallet data */
function getWalletBalance(walletData) {
  const b = walletData?.CURRENT_BALENCE ?? walletData?.CURRENT_BALANCE ?? walletData?.BALANCE ?? walletData?.AMOUNT;
  if (b == null) return 0;
  return typeof b === "string" ? parseFloat(b.replace(/,/g, "")) : parseFloat(b);
}

/** Extract method label from METHOD_NAME (en/ar) */
function getMethodLabel(method) {
  const raw = method?.METHOD_NAME ?? method?.methodName;
  if (!raw) return "Unknown";
  if (typeof raw === "string") {
    try {
      const o = JSON.parse(raw);
      return o.en || o.ar || Object.values(o)[0] || "Unknown";
    } catch {
      return raw;
    }
  }
  if (typeof raw === "object") return raw.en || raw.ar || Object.values(raw)[0] || "Unknown";
  return "Unknown";
}

/** Check if method is Whish (RELATED_TO === 'whish') */
function isWhishMethod(method) {
  const r = (method?.RELATED_TO ?? method?.relatedTo ?? "").toString().toLowerCase();
  return r === "whish";
}

/** Crypto cash-out methods need crypto account / wallet details in Financial Information */
function isCryptoCashOutMethod(method) {
  if (!method || typeof method !== "object") return false;
  const label = getMethodLabel(method).toLowerCase();
  const related = (method?.RELATED_TO ?? method?.relatedTo ?? "").toString().toLowerCase();
  return (
    related === "binance" ||
    related === "crypto" ||
    label.includes("crypto") ||
    label.includes("usdt") ||
    label.includes("bitcoin") ||
    label.includes("ethereum")
  );
}

function getMethodDisplayMeta(methodOption) {
  const label = (methodOption?.label || "").toString().toLowerCase();
  const methodData = methodOption?.data || {};
  const processingRaw =
    methodData?.PROCESSING ?? methodData?.processing ?? methodData?.PROCESSING_TIME ?? "";
  let eta = "";
  if (typeof processingRaw === "string") {
    eta = processingRaw.trim();
  } else if (typeof processingRaw === "object" && processingRaw !== null) {
    eta =
      String(
        processingRaw.en ??
          processingRaw.ar ??
          Object.values(processingRaw)[0] ??
          ""
      ).trim();
  } else if (processingRaw != null) {
    eta = String(processingRaw).trim();
  }

  if (label.includes("bank")) return { icon: "fa-building-columns", eta: eta || "1-3 business days" };
  if (
    label.includes("crypto") ||
    label.includes("usdt") ||
    label.includes("bitcoin") ||
    label.includes("ethereum")
  ) {
    return { icon: "fa-coins", eta: eta || "Instant" };
  }
  if (label.includes("card") || label.includes("visa") || label.includes("master")) {
    return { icon: "fa-credit-card", eta: eta || "Instant" };
  }
  return { icon: "fa-wallet", eta: eta || "Instant" };
}

export default function CashOut() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { success: showSuccess, error: showError, warning: showWarning } = useToast();
  const [amount, setAmount] = useState("");
  const [wallet, setWallet] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [phone, setPhone] = useState("");
  const [wallets, setWallets] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(true);
  const [selectedMethodData, setSelectedMethodData] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [processing, setProcessing] = useState(false);
  const [locationChecking, setLocationChecking] = useState(false);
  const [showPasswordPopup, setShowPasswordPopup] = useState(false);
  const [passwordDialogData, setPasswordDialogData] = useState(null);
  const [showFinancialInfoAction, setShowFinancialInfoAction] = useState(false);
  const [financialBannerApiHint, setFinancialBannerApiHint] = useState(null);

  const permissions = useMemo(() => getPermissionControllers(user), [user]);
  const { customerPerm, customerDevicePerm, brokerPerm, brokerDevicePerm } = permissions;
  const isBroker = user?.TYPE === 3;
  const approvalLocked = useMemo(() => isAccountApprovalLocked(user), [user]);
  const userIsKycVerified = useMemo(() => isKycVerified(user), [user]);

  const hasCashOutPermission =
    hasCustomerPermission(customerPerm, customerDevicePerm, PermissionCustomerModelController.PER__CAN_CASH_OUT) ||
    hasBrokerPermission(brokerPerm, brokerDevicePerm, PermissionBrokerModelController.PER__CAN_CASH_OUT);

  // Require:
  // - Profile completed and NOT in "Account Approval Required" stage (approvalLocked === false)
  // - Cash-out permission
  // - Final KYC verified (VERIFIED / VERIFIED_NOT_CHECKED)
  const canProceed = !approvalLocked && hasCashOutPermission && userIsKycVerified;


  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setErrors({});
        let userData = null;
        try {
          const stored = sessionStorage.getItem("userData");
          const ts = sessionStorage.getItem("userDataTimestamp");
          if (stored && ts && Date.now() - parseInt(ts, 10) < 5 * 60 * 1000) {
            userData = JSON.parse(stored);
            setUser(userData);
          }
        } catch (_) {}
        try {
          const r = await getHomePageInfos();
          const d = r?.decryptedData || r?.data;
          if (d && d.CODE === AppConstants.responseOk && d.DATA) {
            setUser(d.DATA);
            try {
              sessionStorage.setItem("userData", JSON.stringify(d.DATA));
              sessionStorage.setItem("userDataTimestamp", String(Date.now()));
            } catch (_) {}
          }
        } catch (_) {
          // Keep cached user when request fails.
        }

        const [ewData, methodRes] = await Promise.all([
          WalletController.fetchEWalletsWithAmounts().catch(() => ({ wallets: [] })),
          getCashOutPaymentMethod().catch(() => null),
        ]);

        const latestWallets = Array.isArray(ewData?.wallets) ? ewData.wallets : [];

        if (latestWallets.length > 0) {
          const list = latestWallets.map((w) => ({
            value: w.ID ?? w.id,
            label: formatCashWalletDropdownLabel(w, getWalletBalance(w) || 0),
            data: w,
          }));
          setWallets(list);
        } else {
          setWallets([]);
        }

        setPaymentMethodsLoading(true);
        const md = methodRes?.decryptedData || methodRes?.data;
        if (md && md.CODE === AppConstants.responseOk && md.DATA) {
          const arr = Array.isArray(md.DATA) ? md.DATA : [];
          setPaymentMethods(
            arr.map((m) => ({
              value: m.ID ?? m.id,
              label: getMethodLabel(m),
              data: m,
            }))
          );
        } else {
          setPaymentMethods([]);
        }
        setPaymentMethodsLoading(false);
      } catch (e) {
        showError("Error Occurred", e.message || "Failed to load data.");
        setPaymentMethodsLoading(false);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);


  const selectedWalletData = useMemo(() => {
    const v = typeof wallet === "object" ? wallet?.value : wallet;
    return wallets.find((w) => w.value === v)?.data ?? null;
  }, [wallet, wallets]);

  const minAmount = useMemo(
    () => (selectedWalletData ? getMinAmountCashOut(selectedWalletData) : null),
    [selectedWalletData]
  );
  const walletBalance = useMemo(
    () => (selectedWalletData ? getWalletBalance(selectedWalletData) : 0),
    [selectedWalletData]
  );
  const showPhone = useMemo(
    () => (selectedMethodData ? isWhishMethod(selectedMethodData) : false),
    [selectedMethodData]
  );

  const financialMissingDetailMessage = useMemo(() => {
    if (!showFinancialInfoAction) return "";
    const apiMsg = (financialBannerApiHint || "").toString().toLowerCase();
    const bankHints = /bank|iban|swift|wire/.test(apiMsg);
    const cryptoHints =
      /crypto|binance|trc|erc|wallet|usdt|bitcoin|ethereum/.test(apiMsg);

    let needCrypto = false;
    if (cryptoHints && !bankHints) needCrypto = true;
    else if (bankHints && !cryptoHints) needCrypto = false;
    else if (selectedMethodData) needCrypto = isCryptoCashOutMethod(selectedMethodData);
    else needCrypto = false;

    return needCrypto
      ? t("cash_out_financial_missing_crypto", { ns: "mobile" })
      : t("cash_out_financial_missing_bank", { ns: "mobile" });
  }, [showFinancialInfoAction, financialBannerApiHint, selectedMethodData, t]);

  const handleProceed = async () => {
    setProcessing(true);
    setLocationChecking(true);
    setErrors((prev) => ({ ...prev, general: null }));

    const loc = await ensureLocation();
    setLocationChecking(false);
    if (!loc.ok) {
      const msg =
        loc.error === "location_denied"
          ? "Please enable location access to continue."
          : loc.error === "location_unavailable"
            ? "Location is unavailable. Please check your device settings."
            : "Location is required to continue. Please enable it.";
      setProcessing(false);
      showError("Action Required", msg);
      return;
    }

    const newErrors = {};
    const amountRaw = String(amount).trim().replace(/,/g, "");
    const amountVal = Number(amountRaw);
    const isWholeNumber = /^\d+$/.test(amountRaw) && amountVal > 0;
    if (!amount || Number.isNaN(amountVal) || amountVal <= 0) {
      newErrors.amount = "Please enter a valid amount.";
    } else if (!isWholeNumber) {
      newErrors.amount = "Amount must be a whole number. 10.5 is not allowed.";
    } else {
      const minNum = minAmount != null ? Number(minAmount) : NaN;
      const walletNum = Number(walletBalance);
      if (!Number.isNaN(minNum) && amountVal <= minNum) {
        const sym = selectedWalletData?.currencies?.CURRENCY_SYMBOL ?? "";
        newErrors.amount = `Min amount is ${minNum} ${sym}`.trim();
      } else if (!isBroker && !Number.isNaN(walletNum) && amountVal > walletNum) {
        newErrors.amount = "You don't have enough balance.";
      }
    }
    if (!wallet) newErrors.wallet = "Select a wallet.";
    if (!paymentMethod) newErrors.paymentMethod = "Select a cash-out method.";
    if (showPhone && (!phone || !String(phone).trim())) {
      newErrors.phone = "Please enter your phone number (required for Whish).";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      setProcessing(false);
      return;
    }

    try {
      const walletId = typeof wallet === "object" ? wallet.value : wallet;
      const methodId = typeof paymentMethod === "object" ? paymentMethod.value : paymentMethod;
      const payload = {
        amount: amountVal,
        walletId: Number(walletId),
        method: Number(methodId),
        phoneNumber: showPhone ? String(phone).trim() : "",
      };
      const res = await createCashOutOperation(payload);
      const d = res.decryptedData || res.data || {};
      const code = Number(d.CODE);
      if (code === AppConstants.responseOk || code === 200) {
        setShowFinancialInfoAction(false);
        setFinancialBannerApiHint(null);
        setErrors({});
        const reqToken = d.REQUEST_TOKEN;
        const received = typeof d.RECEIVED_AMOUNT === "number" ? d.RECEIVED_AMOUNT : parseFloat(d.RECEIVED_AMOUNT) || 0;
        const sym = selectedWalletData?.currencies?.CURRENCY_SYMBOL ?? "";
        const firstMsg =
          `You will withdraw ${amountVal} ${sym}, you will receive ${received} ${sym} after commission.`;
        setPasswordDialogData({
          requestToken: reqToken,
          firstMessage: firstMsg,
          secondMessage: "",
        });
        setShowPasswordPopup(true);
      } else if (code === AppConstants.responseNotFound || code === 206) {
        showError("Error Occurred", "You don't have access to do this operation.");
      } else if (code === AppConstants.responseWaiting || code === 202) {
        setShowFinancialInfoAction(true);
        setFinancialBannerApiHint(d.MESSAGE ?? d.message ?? null);
        showWarning(
          "Action Required",
          d.MESSAGE ||
            d.message ||
            t("financial_data_should_be_add_to_use_this_method", { ns: "mobile" })
        );
      } else if (code === AppConstants.responseFailed || code === 207) {
        showError("Error Occurred", d.MESSAGE || "You don't have enough amount.");
      } else {
        showError("Error Occurred", d.MESSAGE || "Please try again.");
      }
    } catch (err) {
      showError("Error Occurred", err.message || "Failed to process cash-out. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const refreshLocalWallets = async () => {
    try {
      const ewData = await WalletController.fetchEWalletsWithAmounts();
      if (ewData && Array.isArray(ewData.wallets) && ewData.wallets.length > 0) {
        const list = ewData.wallets.map((w) => ({
          value: w.ID ?? w.id,
          label: formatCashWalletDropdownLabel(w, getWalletBalance(w) || 0),
          data: w,
        }));
        setWallets(list);
      }
    } catch (_) {}
  };

  useEffect(() => {
    const onFundsRefresh = () => {
      refreshLocalWallets();
    };
    window.addEventListener("mfg:funds-refresh", onFundsRefresh);
    return () => window.removeEventListener("mfg:funds-refresh", onFundsRefresh);
  }, []);

  const handlePasswordDialogClose = async (result) => {
    setShowPasswordPopup(false);
    setPasswordDialogData(null);
    if (result === "ok" || result === "need_admin") {
      clearHomePageInfosClientCache();
      showWarning("Request Submitted", result === "ok" ? "Cash-out request submitted successfully." : "Cash-out pending admin approval.");
      setErrors({});
      setAmount("");
      setWallet(null);
      setPaymentMethod(null);
      setSelectedMethodData(null);
      setPhone("");
    } else if (result === "canceled") {
      showWarning("Action Canceled", "Transaction canceled.");
      setErrors({});
      setAmount("");
      setWallet(null);
      setPaymentMethod(null);
      setSelectedMethodData(null);
      setPhone("");
      setShowFinancialInfoAction(false);
      setFinancialBannerApiHint(null);
    } else if (result === "failed") {
      showError("Error Occurred", "Transaction failed. Please try again.");
    }

    // Always refresh wallets to keep balances up to date
    await refreshLocalWallets();
  };

  const handleWalletChange = (val) => {
    setWallet(val);
    if (errors.wallet) setErrors((prev) => ({ ...prev, wallet: null }));
  };

  const handleMethodChange = (val) => {
    setPaymentMethod(val);
    const opt = paymentMethods.find((m) => (m.value === (val?.value ?? val)));
    setSelectedMethodData(opt?.data ?? null);
    setShowFinancialInfoAction(false);
    setFinancialBannerApiHint(null);
    if (errors.paymentMethod) setErrors((prev) => ({ ...prev, paymentMethod: null }));
  };

  return (
    <div className="cash-in-page cash-out-page">
      <Sidebar user={user} />
      <div className="cash-in-content">
        {loading && <div className="cash-in-loading-message">{t("loading", { ns: "mobile" })}</div>}

        <div className="cash-in-container cash-in-container--styled">
          <div className="cash-in-tab-content cash-in-tab-content--styled">
            {!canProceed ? (
              <div className="cash-in-locked-message">
                <i className="fa-solid fa-lock"></i>
                <p>{t("you_dont_have_access_to_do_this_operation", { ns: "mobile" })}</p>
              </div>
            ) : (
              <div className="cash-in-layout-grid">
                <div className="cash-in-form-panel">
                  <div className="cash-in-panel-header">
                    <h2>{t("cash_out_new_withdraw_title", { ns: "mobile" })}</h2>
                    <p>{t("cash_out_new_withdraw_subtitle", { ns: "mobile" })}</p>
                  </div>

                  <div className="cash-in-form">
                <div className="cash-in-form-group" data-field-tour="cash_out:amount">
                  <label className="cash-in-form-label">{t("amount", { ns: "mobile" })}</label>
                  <div className="cash-in-input-wrapper">
                    <span className="cash-in-input-icon">
                      <i className="fa-solid fa-dollar-sign"></i>
                    </span>
                    <CustomInput
                      type="text"
                      inputMode="numeric"
                      placeholder={t("enter_the_amount_to_cashed_out", { ns: "mobile" })}
                      value={amount}
                      onChange={(e) => {
                        const next = String(e.target.value ?? "");
                        if (next !== "" && !/^\d+$/.test(next)) return;
                        setAmount(next);
                        if (errors.amount) setErrors((prev) => ({ ...prev, amount: null }));
                      }}
                      className={`cash-in-input ${errors.amount ? "cash-in-input-error" : ""}`}
                      disabled={processing}
                    />
                  </div>
                  <span className="cash-in-hint">Whole numbers only. For example, 10 is allowed and 10.5 is not.</span>
                  {errors.amount && <div className="cash-in-field-error">{errors.amount}</div>}
                  {minAmount != null && (
                    <span className="cash-in-hint">
                      {t("min_amount_to_cashed_out_is", { ns: "mobile" })} {minAmount} {selectedWalletData?.currencies?.CURRENCY_SYMBOL ?? ""}
                    </span>
                  )}
                </div>

                <div className="cash-in-form-group" data-field-tour="cash_out:wallet">
                  <label className="cash-in-form-label">{t("wallet", { ns: "mobile" })}</label>
                  <CustomDropdown
                    options={wallets}
                    value={wallet}
                    onChange={handleWalletChange}
                    placeholder={t("select_a_wallet", { ns: "mobile" })}
                    className={`cash-in-dropdown ${errors.wallet ? "dropdown-error" : ""}`}
                    disabled={processing}
                    renderOption={(option, isSelected) => {
                      const walletData = typeof option === "object" && option.data ? option.data : null;
                      if (!walletData) {
                        return <span>{typeof option === "object" ? option.label : option}</span>;
                      }
                      const balance =
                        walletData.CURRENT_BALENCE ||
                        walletData.CURRENT_BALANCE ||
                        walletData.BALANCE ||
                        walletData.AMOUNT ||
                        0;
                      const balanceNum =
                        typeof balance === "string"
                          ? parseFloat(balance.replace(/,/g, ""))
                          : parseFloat(balance);
                      const formattedBalance = Number.isNaN(balanceNum) ? 0 : balanceNum;
                      const { flagCode: flagCountryCode, code: currencyCode, sym: currencySymbol, suffix } =
                        resolveCashWalletCurrencyDisplay(
                          walletData,
                          typeof option === "object" ? option.label : ""
                        );
                      return (
                        <div className="cash-in-wallet-option cash-in-wallet-option--currency">
                          <WalletCurrencyFlag
                            countryCode={flagCountryCode}
                            title={currencyCode || currencySymbol || flagCountryCode || ""}
                          />
                          <span>
                            {formattedBalance.toFixed(2)}
                            {suffix ? ` ${suffix}` : ""}
                          </span>
                          {isSelected && <i className="fa-solid fa-check custom-dropdown-check"></i>}
                        </div>
                      );
                    }}
                    getDisplayValue={(option) => {
                      if (!option) return "";
                      if (typeof option === "object" && option.data) {
                        const walletData = option.data;
                        const balance =
                          walletData.CURRENT_BALENCE ||
                          walletData.CURRENT_BALANCE ||
                          walletData.BALANCE ||
                          walletData.AMOUNT ||
                          0;
                        return formatCashWalletDropdownLabel(walletData, balance);
                      }
                      return typeof option === "object" ? option.label : option;
                    }}
                    renderValue={(option, placeholderText) => {
                      if (!option) {
                        return (
                          <span className="custom-dropdown-placeholder">
                            {placeholderText || t("select_a_wallet", { ns: "mobile" })}
                          </span>
                        );
                      }
                      const walletData = typeof option === "object" && option.data ? option.data : null;
                      if (!walletData) {
                        return <span>{typeof option === "object" ? option.label : option}</span>;
                      }
                      const balance =
                        walletData.CURRENT_BALENCE ||
                        walletData.CURRENT_BALANCE ||
                        walletData.BALANCE ||
                        walletData.AMOUNT ||
                        0;
                      const balanceNum =
                        typeof balance === "string"
                          ? parseFloat(balance.replace(/,/g, ""))
                          : parseFloat(balance);
                      const formattedBalance = Number.isNaN(balanceNum) ? 0 : balanceNum;
                      const { flagCode: flagCountryCode, code: currencyCode, sym: currencySymbol, suffix } =
                        resolveCashWalletCurrencyDisplay(
                          walletData,
                          typeof option === "object" ? option.label : ""
                        );
                      return (
                        <div className="cash-in-wallet-value cash-in-wallet-value--currency">
                          <WalletCurrencyFlag
                            countryCode={flagCountryCode}
                            title={currencyCode || currencySymbol || flagCountryCode || ""}
                          />
                          <span>
                            {formattedBalance.toFixed(2)}
                            {suffix ? ` ${suffix}` : ""}
                          </span>
                        </div>
                      );
                    }}
                  />
                </div>

                <div className="cash-in-form-group" data-field-tour="cash_out:method">
                  <label className="cash-in-form-label">{t("cash_out_method", { ns: "mobile" })}</label>
                  <div className={`cash-in-method-list ${errors.paymentMethod ? "cash-in-method-list--error" : ""}`}>
                    {paymentMethodsLoading ? (
                      <>
                        <div className="cash-in-method-skeleton" />
                        <div className="cash-in-method-skeleton" />
                        <div className="cash-in-method-skeleton" />
                      </>
                    ) : paymentMethods.length > 0 ? (
                      paymentMethods.map((methodOption) => {
                        const selectedValue =
                          typeof paymentMethod === "object" ? paymentMethod?.value : paymentMethod;
                        const methodValue =
                          typeof methodOption === "object" ? methodOption?.value : methodOption;
                        const isSelected = selectedValue === methodValue;
                        const meta = getMethodDisplayMeta(methodOption);

                        return (
                          <button
                            key={String(methodValue)}
                            type="button"
                            className={`cash-in-method-item ${isSelected ? "cash-in-method-item--active" : ""}`}
                            onClick={() => handleMethodChange(methodOption)}
                            disabled={processing}
                          >
                            <span className="cash-in-method-item-left">
                              <span
                                className={`cash-in-method-radio ${isSelected ? "is-selected" : ""}`}
                                aria-hidden="true"
                              />
                              <i className={`fa-solid ${meta.icon}`} />
                              <span>{methodOption?.label || t("cash_out_method_fallback", { ns: "mobile" })}</span>
                            </span>
                            <span className="cash-in-method-item-right">{meta.eta}</span>
                          </button>
                        );
                      })
                    ) : (
                      <div className="cash-in-method-empty">
                        {t("cash_out_no_methods_available", { ns: "mobile" })}
                      </div>
                    )}
                  </div>
                </div>
                {showFinancialInfoAction && (
                  <div className="cash-in-form-group cash-in-financial-banner-wrap">
                    <StatusBanner
                      informational={{
                        message: financialMissingDetailMessage,
                        buttonText: t("apply", { ns: "mobile" }),
                        buttonIcon: "fa-file-lines",
                        onButtonClick: () => navigate("/pages/financial-information"),
                      }}
                    />
                  </div>
                )}

                {showPhone && (
                  <div className="cash-in-form-group" data-field-tour="cash_out:phone">
                    <label className="cash-in-form-label">{t("mobile_number", { ns: "mobile" })}</label>
                    <div className={`phone-input-wrapper ${errors.phone ? "input-error" : ""}`}>
                      <PhoneInput
                        country="us"
                        value={phone}
                        disableCountryGuess
                        enableSearch
                        excludeCountries={["il"]}
                        containerStyle={{ width: "100%" }}
                        inputStyle={{ width: "100%" }}
                        buttonStyle={{ borderTopLeftRadius: 8, borderBottomLeftRadius: 8 }}
                        onChange={(value) => {
                          setPhone(value || "");
                          if (errors.phone) {
                            setErrors((prev) => ({ ...prev, phone: null }));
                          }
                        }}
                        disabled={processing}
                      />
                    </div>
                    {errors.phone && <div className="cash-in-field-error">{errors.phone}</div>}
                  </div>
                )}
                  </div>

                  <div className="cash-in-actions">
                    <CustomButton
                      className="cash-in-proceed-button"
                      variant="primary"
                      onClick={handleProceed}
                      disabled={processing}
                    >
                      <span>
                        {locationChecking
                          ? t("cash_in_getting_location", { ns: "mobile" })
                          : processing
                            ? t("loading", { ns: "mobile" })
                            : t("request", { ns: "mobile" })}
                      </span>
                    </CustomButton>
                  </div>
                </div>

                <div className="cash-in-side-panel">
                  <div className="cash-in-info-card">
                    <h3>
                      <i className="fa-solid fa-clock" />
                      {t("cash_in_processing_time", { ns: "mobile" })}
                    </h3>
                    <ul className="cash-in-processing-list">
                      {paymentMethodsLoading ? (
                        <li>
                          <span>
                            <i className="fa-solid fa-clock" />
                            {t("cash_in_loading_methods", { ns: "mobile" })}
                          </span>
                          <strong>...</strong>
                        </li>
                      ) : paymentMethods.length > 0 ? (
                        paymentMethods.map((methodOption) => {
                          const meta = getMethodDisplayMeta(methodOption);
                          const methodValue =
                            typeof methodOption === "object" ? methodOption?.value : methodOption;
                          const selectedValue =
                            typeof paymentMethod === "object" ? paymentMethod?.value : paymentMethod;
                          const isActive = selectedValue === methodValue;

                          return (
                            <li key={`cashout-processing-${String(methodValue)}`} className={isActive ? "active" : ""}>
                              <span>
                                <i className={`fa-solid ${meta.icon}`} />
                                {methodOption?.label || t("cash_out_method_fallback", { ns: "mobile" })}
                              </span>
                              <strong>{meta.eta}</strong>
                            </li>
                          );
                        })
                      ) : (
                        <li>
                          <span>
                            <i className="fa-solid fa-clock" />
                            {t("cash_in_no_method_selected", { ns: "mobile" })}
                          </span>
                          <strong>-</strong>
                        </li>
                      )}
                    </ul>
                  </div>

                  <div className="cash-in-help-card">
                    <h3>
                      <i className="fa-solid fa-headset" />
                      {t("contactus_title", { ns: "mobile" })}
                    </h3>
                    <p>
                      {t("cash_in_help_description", { ns: "mobile" })}
                    </p>
                    <button
                      type="button"
                      onClick={() =>
                        openSupportChatOnCurrentPage(
                          navigate,
                          location.pathname,
                          location.search
                        )
                      }
                    >
                      {t("about_live_chat_value", { ns: "mobile" })}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <TransactionPasswordDialog
        open={showPasswordPopup && !!passwordDialogData}
        onClose={handlePasswordDialogClose}
        mode="cashout"
        requestToken={passwordDialogData?.requestToken ?? ""}
        firstMessage={passwordDialogData?.firstMessage ?? ""}
        secondMessage={passwordDialogData?.secondMessage ?? ""}
      />
    </div>
  );
}
